/**
 * Увольнение (03.10, владелец: «увольнение само снимает всю неначатую будущую работу;
 * уволенному ничего не пишем; ОО видит „снят с N игр, пересоберите расстановку“»).
 * ConfirmModal tone=danger (A3): сначала превью с сервера — что снимется (игры, МК,
 * админ зала, дежурства ДК, допработы) и список игр; причина — Dropdown пресетов,
 * «Другое…» — TextPad. Сервер может запретить (себя, последнего ИО) — тогда кнопка
 * гаснет и видна причина. После увольнения та же модалка показывает итог «Снят с N
 * игр — пересоберите расстановку» и список, одна кнопка «Понятно».
 */
import { useEffect, useState } from "react";
import { ConfirmModal, Dropdown, TextPad } from "../../components/shared";
import { fmtDay, type FireCounts, type FireGame, type FirePreview, type FireResult, type StaffRow } from "../../hooks/staffApi";
import css from "./fire.module.css";

type Res<T> = { ok: true; value: T } | { ok: false; error: string };

interface Props {
  row: StaffRow;
  preview: (id: string) => Promise<Res<FirePreview>>;
  fire: (id: string, reason: string) => Promise<Res<FireResult>>;
  onClose: () => void;
}

const REASONS = ["По собственному желанию", "Нарушение дисциплины", "Не выходит на связь", "Конец сезона"];
const OTHER = "other";
/** Формы слова: 1 игра, 2 игры, 5 игр. */
const FORMS: [keyof FireCounts, [string, string, string]][] = [
  ["assignments", ["игра", "игры", "игр"]], ["mc", ["МК", "МК", "МК"]], ["hall_admin", ["смена админа зала", "смены админа зала", "смен админа зала"]],
  ["dc_duty", ["дежурство ДК", "дежурства ДК", "дежурств ДК"]], ["extra_work", ["допработа", "допработы", "допработ"]],
];
function plural(n: number, f: [string, string, string]): string {
  const d = n % 10, h = n % 100;
  return `${n} ${d === 1 && h !== 11 ? f[0] : d >= 2 && d <= 4 && (h < 12 || h > 14) ? f[1] : f[2]}`;
}
const listOf = (c: FireCounts, skipGames = false): string[] =>
  FORMS.filter(([k]) => c[k] > 0 && !(skipGames && k === "assignments")).map(([k, f]) => plural(c[k], f));

function Games({ games }: { games: FireGame[] }) {
  if (!games.length) return null;
  return (
    <ul className={css.games}>
      {games.map((g) => (
        <li key={g.bookingId}><span className={css.gDate}>{fmtDay(g.date)} {g.time}</span><span className={css.gQuest}>{g.quest}</span></li>
      ))}
    </ul>
  );
}

export function FireDialog({ row, preview, fire, onClose }: Props) {
  const [pv, setPv] = useState<FirePreview | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [dd, setDd] = useState<HTMLElement | null>(null);
  const [pad, setPad] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<FireResult | null>(null);

  useEffect(() => {
    let live = true;
    void preview(row.id).then((r) => { if (live) { if (r.ok) setPv(r.value); else setLoadErr(r.error); } });
    return () => { live = false; };
  }, [row.id, preview]);

  const go = async () => {
    setBusy(true); setErr(null);
    const r = await fire(row.id, reason);
    setBusy(false);
    if (r.ok) setDone(r.value); else setErr(r.error);
  };

  if (done) {
    const also = done.removed ? listOf(done.removed, true) : [];
    return (
      <ConfirmModal tone="warn" solo title={`Уволен: ${row.name}`} actionLabel="Понятно" onAction={onClose} onCancel={onClose}>
        <div className={css.lead}>Вход закрыт.{done.notice || also.length ? "" : " Будущей работы не было."}</div>
        {done.notice ? <div className={css.notice}>{done.notice.replace(", ", " — ")}</div> : null}
        {also.length ? <div className={css.what}>Также снято: <b>{also.join(", ")}</b></div> : null}
        <Games games={done.games} />
      </ConfirmModal>
    );
  }

  const parts = pv ? listOf(pv.counts) : [];
  const blocked = pv?.blocked ?? null;
  return (
    <>
      <ConfirmModal tone="danger" title={`Уволить: ${row.name}?`} actionLabel={busy ? "Увольняем…" : "Уволить"}
        actionDisabled={busy || !pv || !!blocked} cancelDisabled={busy} onAction={() => void go()} onCancel={onClose}>
        <div className={css.lead}>Вход в кабинет закроется сразу. Сообщение уволенному не уходит.</div>
        {!pv && !loadErr ? <div className={css.muted}>Считаем будущую работу…</div> : null}
        {loadErr ? <div className={css.err}>{loadErr}</div> : null}
        {pv ? (
          <div className={css.what}>
            {parts.length ? <>Будет снято: <b>{parts.join(", ")}</b></> : "Будущей работы нет, снимать нечего."}
          </div>
        ) : null}
        {pv ? <Games games={pv.games} /> : null}
        {blocked ? <div className={css.err}>{blocked}</div> : null}
        {pv && !blocked ? (
          <div className={css.reasonRow}>
            <span className={css.lbl}>Причина</span>
            <button type="button" className={`${css.reason} ${reason ? "" : css.reasonEmpty}`} disabled={busy} onClick={(e) => setDd(e.currentTarget)}
              aria-label={`Причина: ${reason || "не указана, необязательно"}`}>
              {reason || "необязательно"}
            </button>
          </div>
        ) : null}
        {err ? <div className={css.err}>{err}</div> : null}
      </ConfirmModal>
      <Dropdown open={!!dd} anchor={dd} multi={false} title="Причина" selectedIds={REASONS.includes(reason) ? [reason] : reason ? [OTHER] : []}
        items={[...REASONS.map((r) => ({ id: r, label: r })), { id: OTHER, label: "Другое…" }]}
        onCommit={(ids) => { const id = String(ids[0] ?? ""); if (id === OTHER) setPad(true); else setReason(id); }}
        onClose={() => setDd(null)} />
      <TextPad open={pad} label="Причина увольнения" value={REASONS.includes(reason) ? "" : reason} maxLength={300} multiline
        placeholder="Коротко, для журнала" onCommit={(v) => setReason(v.trim())} onClose={() => setPad(false)} />
    </>
  );
}
